import { ScannedFile } from './types';

/**
 * Recursively walks a DataTransferItemList from a drop event using the
 * (non-standard but universally supported) File and Directory Entries API,
 * so dropped folders — including nested sub-folders, and any mix of files
 * and folders in one drop — are flattened into a file list with their
 * original relative paths preserved.
 */
export async function scanDataTransferItems(items: DataTransferItemList): Promise<ScannedFile[]> {
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }

  const out: ScannedFile[] = [];
  await Promise.all(entries.map((entry) => walkEntry(entry, '', out)));
  return out;
}

function walkEntry(entry: FileSystemEntry, prefix: string, out: ScannedFile[]): Promise<void> {
  if (entry.isFile) {
    return new Promise((resolve, reject) => {
      (entry as FileSystemFileEntry).file((file) => {
        out.push({ file, relativePath: `${prefix}${entry.name}` });
        resolve();
      }, reject);
    });
  }

  if (entry.isDirectory) {
    return readAllDirectoryEntries(entry as FileSystemDirectoryEntry).then((children) =>
      Promise.all(children.map((child) => walkEntry(child, `${prefix}${entry.name}/`, out))).then(() => undefined),
    );
  }

  return Promise.resolve();
}

/**
 * `readEntries()` does not guarantee returning every entry in one call (the
 * spec requires callers to keep calling it until it returns an empty array),
 * which matters for large directories.
 */
function readAllDirectoryEntries(directoryEntry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = directoryEntry.createReader();
  const all: FileSystemEntry[] = [];

  return new Promise((resolve, reject) => {
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

/**
 * Handles the GUI file/folder <input> pickers. Directory inputs
 * (webkitdirectory) populate each File's webkitRelativePath automatically.
 */
export function scanFileList(fileList: FileList): ScannedFile[] {
  return Array.from(fileList).map((file) => ({
    file,
    relativePath: file.webkitRelativePath || file.name,
  }));
}

/** Extracts pasted images/screenshots (Ctrl+V) from a ClipboardEvent. */
export function scanClipboardItems(items: DataTransferItemList): ScannedFile[] {
  const out: ScannedFile[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (!file) continue;
    const name = file.name && file.name !== 'image.png' ? file.name : `pasted-${Date.now()}.png`;
    out.push({ file, relativePath: name });
  }
  return out;
}
